import {
  IItinerary,
  IFareBreakdown,
  IFlightResult,
  IFlightSegment,
  getMinutes,
} from "@thekilroygroup/shared-ui";
import { ILightItinerary } from "redux/filters";

/**
 * @param itinerary Check if an itinerary has allowed checked in baggage
 * @returns boolean
 */
const checkedBaggageAllowed = (itinerary: IItinerary) => {
  return itinerary.baggageAllowed || false;
};

/**
 * @param itinerary Check if an itinerary is Youth or Student ticket
 * @returns "YOUTHSTUDENT" | ORDINARY
 */
const getTravellerType = (itinerary: IItinerary) => {
  if (
    itinerary.fareBreakdowns?.some(
      (fareBreakdown: IFareBreakdown, i: number) =>
        fareBreakdown.passengerType === "STU" ||
        fareBreakdown.passengerType === "YTH",
    )
  ) {
    return "YOUTHSTUDENT";
  }

  return "ORDINARY";
};

/**
 * This function takes a list of itineraries as an argument.
 * To speed up filtering a long list of itineraries - we trim the itinerary for all the data that isn't necessary for filtering and sorting. Later before rendering the search results - we select the full itineraries with the function
 * @param itineraries
 */
export const buildLightItineraries = (itineraries: IItinerary[]) => {
  const lightItineraries: ILightItinerary[] = itineraries.map(
    (itinerary: IItinerary, index: number) => {
      const lightItinerary: ILightItinerary = {
        index: index,
        id: itinerary.id,
        checkedBaggage: checkedBaggageAllowed(itinerary),
        travellerType: getTravellerType(itinerary),
        stops: Math.max(
          ...itinerary.flights.map(
            (flight: IFlightResult) => flight.flightSegments.length - 1,
          ),
        ),
        departureTime: itinerary.flights.map((flight: IFlightResult) => {
          return {
            departure: getMinutes(flight.departureDateTime),
            arrival: getMinutes(flight.arrivalDateTime),
          };
        }),
        duration: itinerary.flights.map(
          (flight: IFlightResult) => flight.flightTime,
        ),
        price: itinerary.totalFareAmounts.total,
        airlines: getAirlinesFromItinerary(itinerary, "marketingAirline"),
        isDuplicate: "paired" in itinerary,
      };

      return lightItinerary;
    },
  );
  return lightItineraries;
};

/**
 * Get an array of all airlines in a list of itineraries
 * @param itineraries
 */
export const getAllAirlines = (
  itineraries: IItinerary[],
  airlineType: "marketingAirline" | "operatingAirline",
) => {
  const uniqueAirlinesArray: string[] = [];

  itineraries.forEach((itinerary: IItinerary) => {
    itinerary.flights.forEach((flight: IFlightResult) => {
      flight.flightSegments.forEach((segment: IFlightSegment) => {
        const airline = segment[airlineType];

        if (!uniqueAirlinesArray.includes(airline)) {
          uniqueAirlinesArray.push(airline);
        }
      });
    });
  });
  return uniqueAirlinesArray;
};

/**
 * Get an array of all airlines an itinerary
 * @param itineraries
 */
export const getAirlinesFromItinerary = (
  itinerary: IItinerary,
  airlineType: "marketingAirline" | "operatingAirline",
) => {
  const uniqueAirlinesArray: string[] = [];

  itinerary.flights.forEach((flight: IFlightResult) => {
    flight.flightSegments.forEach((segment: IFlightSegment) => {
      const airline = segment[airlineType];

      if (!uniqueAirlinesArray.includes(airline)) {
        uniqueAirlinesArray.push(airline);
      }
    });
  });

  return uniqueAirlinesArray;
};
